import { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useProviderService, { useProviderServiceForm } from '@hooks/useProviderService'

import EntitySelectBox from '@components/EntitySelectBox'

const confirmDelete = (deleteProviderService, deleteFn, closeModal) => {
  const deleteProviderServicePayload = {
    callbacks: {
      action: deleteFn,
      afterAction: closeModal,
    },
    toastText: 'Service Deleted',
  }

  swal
    .fire({
      title: 'Remove Service?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it.',
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        deleteProviderService(deleteProviderServicePayload)
      }
    })
}

const createOrUpdateProviderService = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createProviderService, updateProviderService },
  } = props

  const text = entityState.id ? 'Updated' : 'Created'
  const actionFn = entityState.id ? updateFn : createFn

  const ProviderServicePayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Service ${text}`,
  }

  if (entityState.id) return updateProviderService(ProviderServicePayload)

  return createProviderService(ProviderServicePayload)
}

const CreateOrEditProviderServiceModal = (props) => {
  const { callbacks, modalKey, showModal } = props

  const { closeModal, deleteProviderService } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { providerService } = modalPayload

  const {
    callbacks: {
      createProviderService: createFn,
      deleteProviderService: deleteFn,
      updateProviderService: updateFn,
    },
    creating,
    deleting,
    provider,
    updating,
  } = useProviderService(providerService)

  const {
    entityState,
    entityState: {
      id,
      deadline,
      deep_link_id,
      service_id,
    },
    errors,
    handlers,
    saveEnabled,
  } = useProviderServiceForm(providerService)

  const saveOrCreateButtonText = id ? 'Save Service' : 'Add Service'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={id ? 'Edit' : 'Add'} titleSecondLine="Service" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Service" errorMessage={errors.service_id}>
            <EntitySelectBox
              entityKey="services"
              name="service_id"
              value={service_id}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="Deadline" marginTop="large">
            <input name="deadline" value={deadline} type="time" {...handlers} />
          </Form.Field>

          <Form.Field label="Deep Link ID" marginTop="large">
            <select name="deep_link_id" value={deep_link_id} {...handlers}>
              <option value="">Please Select...</option>
              {provider?.deep_links?.map(deepLink => (
                <option
                  key={deepLink.id}
                  value={deepLink.id}
                >
                  {deepLink.title} - #{deepLink.id}
                </option>
              ))}
            </select>
          </Form.Field>

          {id && (
            <Form.Field label="More Options" marginTop="large">
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={faTrashAlt} />}
                loading={deleting}
                onClick={() => confirmDelete(deleteProviderService, deleteFn, closeModal)}
                size="medium"
              >
                Remove Service
              </Button>
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={() => createOrUpdateProviderService(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveEnabled ? saveOrCreateButtonText : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditProviderServiceModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

CreateOrEditProviderServiceModal.defaultProps = {
  modalKey: 'CreateOrEditProviderServiceModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProviderServiceModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
